import { html, nothing } from "lit";
import type { WorkspaceTreeNode } from "../controllers/chat-workspace.ts";
import type {
  SessionArtifactDetail,
  SessionArtifactRecord,
} from "../controllers/session-artifacts.ts";
import type { SessionFileRecord } from "../chat/session-files.ts";
import {
  renderSessionUploadsPanel,
  type SessionUploadsPanelProps,
} from "./fleet-session-uploads-panel.ts";

export type FleetSessionInspectorFilesProps = {
  loading: boolean;
  error: string | null;
  changes: SessionArtifactRecord[];
  selectedChangeId: string | null;
  detailLoading: boolean;
  detail: SessionArtifactDetail | null;
  connected: boolean;
  tree: WorkspaceTreeNode[];
  selectedPath: string | null;
  fileContent: string | null;
  fileLoading: boolean;
  editContent: string;
  writeSaving: boolean;
  onRefresh: () => void;
  onSelectChange: (id: string) => void;
  onSelectFile: (path: string) => void;
  onEditContentChange: (content: string) => void;
  onSaveFile: () => void;
  /**
   * Inbound attachments for this session, rendered ABOVE the change list.
   *
   * Project diffs and session uploads are different stores: an operator who
   * attached an mp4 changed no project file, so the change list legitimately
   * reads "No recorded changes yet" while the session has an upload the
   * agent already analysed. Both belong on this tab; conflating them is
   * what made the Files pane look empty after a successful attach.
   */
  uploads?: SessionUploadsPanelProps;
};

function opLabel(op: SessionArtifactRecord["op"]): string {
  switch (op) {
    case "created":
      return "created";
    case "deleted":
      return "deleted";
    case "manual":
      return "manual";
    default:
      return "modified";
  }
}

function flattenFiles(
  nodes: WorkspaceTreeNode[],
  depth = 0,
): Array<{ node: WorkspaceTreeNode; depth: number }> {
  const out: Array<{ node: WorkspaceTreeNode; depth: number }> = [];
  for (const node of nodes) {
    if (node.kind === "file") {
      out.push({ node, depth });
    }
    if (node.children?.length) {
      out.push(...flattenFiles(node.children, depth + 1));
    }
  }
  return out;
}

export function renderFleetSessionInspectorFilesTab(props: FleetSessionInspectorFilesProps) {
  const files = flattenFiles(props.tree);

  return html`
    <div class="session-inspector-files">
      ${
        props.uploads
          ? renderSessionUploadsPanel(props.uploads)
          : html`
              <div class="session-inspector-files__list-title muted">Session uploads</div>
              <p class="muted">Session uploads are unavailable in this view.</p>
            `
      }
      <div class="session-inspector-files__toolbar">
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${!props.connected || props.loading}
          @click=${props.onRefresh}
        >
          Refresh changes
        </button>
      </div>
      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}
      <div class="session-inspector-files__grid">
        <div class="session-inspector-files__list" role="list">
          <div class="session-inspector-files__list-title muted">Project changes</div>
          ${
            props.loading
              ? html`
                  <p class="muted">Loading…</p>
                `
              : props.changes.length === 0
                ? html`
                    <p class="muted">
                      No project changes recorded yet. Tool write/edit/apply_patch and manual saves
                      appear here — files you attached in the composer are listed above under Session
                      uploads.
                    </p>
                  `
                : props.changes.map(
                    (c) => html`
                      <button
                        type="button"
                        role="listitem"
                        class="session-inspector-files__change ${props.selectedChangeId === c.id ? "session-inspector-files__change--active" : ""}"
                        @click=${() => props.onSelectChange(c.id)}
                      >
                        <span class="session-inspector-files__change-op">${opLabel(c.op)}</span>
                        <span class="session-inspector-files__change-path mono">${c.path}</span>
                        <span class="session-inspector-files__change-meta muted">${c.tool}</span>
                      </button>
                    `,
                  )
          }
        </div>
        <div class="session-inspector-files__diff">
          ${
            props.detailLoading
              ? html`
                  <p class="muted">Loading diff…</p>
                `
              : props.detail
                ? html`
                  <div class="session-inspector-files__diff-header mono muted">${props.detail.change.path}</div>
                  <pre class="session-inspector-files__diff-pre mono">${props.detail.diff}</pre>
                `
                : html`
                    <p class="muted">Select a change to view diff.</p>
                  `
          }
        </div>
      </div>
      <div class="session-inspector-files__editor">
        <div class="session-inspector-files__list-title muted">Edit sandbox file</div>
        <div class="session-inspector-files__file-pick">
          ${
            files.length === 0
              ? html`
                  <span class="muted">No files in workspace tree.</span>
                `
              : nothing
          }
          ${files.map(
            ({ node, depth }) => html`
              <button
                type="button"
                class="session-inspector-files__file-btn ${props.selectedPath === node.path ? "session-inspector-files__file-btn--active" : ""}"
                style="padding-left:${8 + depth * 12}px"
                @click=${() => props.onSelectFile(node.path)}
              >
                ${node.name}
              </button>
            `,
          )}
        </div>
        ${
          props.selectedPath
            ? html`
              <label class="session-inspector-files__edit-label">
                <span class="mono muted">${props.selectedPath}</span>
                <textarea
                  class="session-inspector-files__textarea mono"
                  .value=${props.editContent}
                  ?disabled=${props.fileLoading}
                  @input=${(e: Event) =>
                    props.onEditContentChange((e.target as HTMLTextAreaElement).value)}
                ></textarea>
              </label>
              <button
                type="button"
                class="btn"
                ?disabled=${!props.connected || props.writeSaving || props.fileLoading}
                @click=${props.onSaveFile}
              >
                ${props.writeSaving ? "Saving…" : "Save to workspace"}
              </button>
            `
            : nothing
        }
      </div>
    </div>
  `;
}
